import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import MyContext from '@/shared/contexts/AppContext';
import {
  BuscarDefensorPorCPF,
  BuscarRelatoriosDoDefensor,
  BuscarTodasLotacoesDefensor,
} from '@/modules/reports/application/relatorios-atividades.service';
import { getTokenPayload } from '@/modules/reports/application/relatorios-atividades.utils';
import { BuscarRelatoriosParaAprovacao } from '@/modules/reports/application/relatorios-aprovacao.service';
import {
  buildNotificacoesDefensor,
  buildNotificacoesDiretoria,
  loadIdsOcultasParaChave,
  saveIdsOcultasParaChave,
  STORAGE_KEY_OCULTAS_DEFENSOR,
  STORAGE_KEY_OCULTAS_DIRETORIA,
  storageKeyOcultasPorModo,
  type RelatorioNotificacaoItem,
  type RelatoriosNotificacoesModo,
} from '@/modules/reports/application/relatorios-notificacoes.utils';
import { GetDiretoriaDoUsuario } from '@/modules/structure/diretoria/diretoria.service';
import type { ReadLotacaoDto } from '@/api.types';
import type { RelatorioAtividadeDto } from '@/modules/reports/application/relatorios-atividades.types';

type UseRelatoriosNotificacoesBellOptions = {
  /** Quando true, força um reload (ex.: ao abrir o dropdown). */
  refreshToken?: number;
};

export function useRelatoriosNotificacoesBell(options: UseRelatoriosNotificacoesBellOptions = {}) {
  const { refreshToken = 0 } = options;
  const context = useContext(MyContext);
  const cpf = context?.state?.cpf ?? getTokenPayload()?.personCPF ?? '';

  const [loading, setLoading] = useState(false);
  const [itensBrutos, setItensBrutos] = useState<RelatorioNotificacaoItem[]>([]);
  const [idsOcultosDefensor, setIdsOcultosDefensor] = useState<Set<string>>(() =>
    loadIdsOcultasParaChave(STORAGE_KEY_OCULTAS_DEFENSOR),
  );
  const [idsOcultosDiretoria, setIdsOcultosDiretoria] = useState<Set<string>>(() =>
    loadIdsOcultasParaChave(STORAGE_KEY_OCULTAS_DIRETORIA),
  );

  const loadingRef = useRef(false);

  const carregar = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      const itens: RelatorioNotificacaoItem[] = [];

      let defensorId: number | null = null;
      if (cpf) {
        try {
          const defensor = await BuscarDefensorPorCPF(cpf);
          if (defensor?.id) {
            defensorId = defensor.id;
            const [lotacoes, relatorios] = await Promise.all([
              BuscarTodasLotacoesDefensor(defensor.id) as Promise<ReadLotacaoDto[]>,
              BuscarRelatoriosDoDefensor(defensor.id) as Promise<RelatorioAtividadeDto[]>,
            ]);
            itens.push(...buildNotificacoesDefensor(relatorios ?? [], lotacoes ?? []));
          }
        } catch {
          // sem perfil de defensor — ignora
        }
      }

      try {
        const diretoria = await GetDiretoriaDoUsuario();
        if (diretoria?.id) {
          const lista = await BuscarRelatoriosParaAprovacao(diretoria.id);
          const filtrada =
            defensorId != null ? lista.filter((r) => r.defensorId !== defensorId) : lista;
          const pendentes = filtrada.filter((r) => r.status === 'enviado').length;
          itens.push(...buildNotificacoesDiretoria(filtrada, pendentes, diretoria.id));
        }
      } catch {
        // sem diretoria — ignora
      }

      setItensBrutos(itens);
      setIdsOcultosDefensor(loadIdsOcultasParaChave(STORAGE_KEY_OCULTAS_DEFENSOR));
      setIdsOcultosDiretoria(loadIdsOcultasParaChave(STORAGE_KEY_OCULTAS_DIRETORIA));
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [cpf]);

  useEffect(() => {
    void carregar();
  }, [carregar, refreshToken]);

  const notificacoes = useMemo(() => {
    return itensBrutos.filter((n) => {
      if (n.modo === 'diretoria') return !idsOcultosDiretoria.has(n.id);
      return !idsOcultosDefensor.has(n.id);
    });
  }, [itensBrutos, idsOcultosDefensor, idsOcultosDiretoria]);

  const ocultarNotificacao = useCallback((item: RelatorioNotificacaoItem) => {
    const chave = storageKeyOcultasPorModo(item.modo);
    if (item.modo === 'diretoria') {
      setIdsOcultosDiretoria((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        saveIdsOcultasParaChave(chave, next);
        return next;
      });
    } else {
      setIdsOcultosDefensor((prev) => {
        const next = new Set(prev);
        next.add(item.id);
        saveIdsOcultasParaChave(chave, next);
        return next;
      });
    }
  }, []);

  const ocultarVarias = useCallback((items: RelatorioNotificacaoItem[]) => {
    const porModo: Record<RelatoriosNotificacoesModo, string[]> = {
      defensor: [],
      diretoria: [],
    };
    for (const item of items) {
      porModo[item.modo].push(item.id);
    }
    if (porModo.defensor.length > 0) {
      setIdsOcultosDefensor((prev) => {
        const next = new Set(prev);
        porModo.defensor.forEach((id) => next.add(id));
        saveIdsOcultasParaChave(STORAGE_KEY_OCULTAS_DEFENSOR, next);
        return next;
      });
    }
    if (porModo.diretoria.length > 0) {
      setIdsOcultosDiretoria((prev) => {
        const next = new Set(prev);
        porModo.diretoria.forEach((id) => next.add(id));
        saveIdsOcultasParaChave(STORAGE_KEY_OCULTAS_DIRETORIA, next);
        return next;
      });
    }
  }, []);

  return {
    loading,
    notificacoes,
    count: notificacoes.length,
    refresh: carregar,
    ocultarNotificacao,
    ocultarVarias,
  };
}
